// @flow
import { useState, useEffect, useRef } from 'react';

/**
 * Animates from 0 to `target` once `active` becomes true.
 * Returns the current display value as a number.
 */
export default function useCounter(target: number, duration: number = 1600, active: boolean = false): number {
  const [value, setValue] = useState(0);
  const raf = useRef(null);
  const start = useRef(null);

  useEffect(() => {
    if (!active) return undefined;

    const ease = (t) => t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;

    const tick = (now) => {
      if (!start.current) start.current = now;
      const elapsed = now - start.current;
      const progress = Math.min(elapsed / duration, 1);
      setValue(Math.round(ease(progress) * target));
      if (progress < 1) {
        raf.current = requestAnimationFrame(tick);
      }
    };

    raf.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf.current);
  }, [active, target, duration]);

  return value;
}
